import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { useForm } from "react-hook-form";
import { structuralGroupService } from "../services/structuralGroups";
import { levelOfStructureService } from "@modules/organizationalStructure/levelOfStructure/services/levelOfStructure";
import { companiesService } from "@shared/services/companiesService";
import { nomenclaturesOz } from "@modules/organizationalStructure/nomenclatures/services/nomenclaturesOz";
import { positionsService } from "@modules/organizationalStructure/positions/services/positions";
import LoadingButton from "@shared/components/LoadingButton";
import CustomDatePicker from "@shared/components/CustomDatePicker";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

export default function StructuralGroupForm({ onClose, onConfirm, editData }) {
    const { t } = useTranslation();
    const modalRef = useRef(null);

    const { selectedCompany, parentId, fullCompanyObject, companies, isDrillDownMode, selectedRecord, breadcrumbPath } = useSelector(state => state.structuralGroup);
    

    
    const selectedCompanyObject = fullCompanyObject || companies.find(c => c.id === selectedCompany) || { id: selectedCompany, name: 'Company' };
    const [loading, setLoading] = useState(false);
    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [levels, setLevels] = useState([]);
    const [levelsLoading, setLevelsLoading] = useState(false);
    const [companyCenters, setCompanyCenters] = useState([]);
    const [companyCentersLoading, setCompanyCentersLoading] = useState(false);
    const [typeOptions, setTypeOptions] = useState([]);
    const [segmentOptions, setSegmentOptions] = useState([]);
    const [subordinationOptions, setSubordinationOptions] = useState([]);
    const [isIndefinite, setIsIndefinite] = useState(true);

    const validationSchema = yup.object().shape({
        companyId: yup.string().required(t('common.required')),
        abbreviation: yup.string().required(t('common.required')),
        level: yup.string().required(t('common.required')),
        name: yup.string().required(t('common.required')),
        location: yup.string().required(t('common.required')),
        expensesCenter: yup.string().required(t('common.required')),
        siblingPriority: yup.string().required(t('common.required')),
        validityFrom: yup.date().required(t('common.required')),
        validityTo: yup.date().when('indefinite', {
            is: false,
            then: (schema) => schema.required(t('common.required')),
            otherwise: (schema) => schema.notRequired()
        }),
        indefinite: yup.boolean(),
    });

    const { register, handleSubmit, formState: { errors }, reset, trigger, setValue, watch } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        criteriaMode: 'all',
        defaultValues: {
            validityFrom: new Date(),
            indefinite: true,
            companyId: selectedCompany || ''
        }
    });
    const selectedLocationId = watch('location');
    const watchedNomenclatureValue = watch('nomenclatureItemByTypeOfDivisionId');
    const watchedSegmentValue = watch('nomenclatureItemBySegmentId');
    const watchedSubordinationValue = watch('addSubordination');
    const watchedLevelValue = watch('level');
    const watchedExpensesCenterValue = watch('expensesCenter');
    const watchedLocationValue = watch('location');
    const indefiniteValue = watch('indefinite');
    const watchedValidityTo = watch('validityTo');

    const companyName = selectedCompanyObject?.name || 'No company selected';
    
    // Calculate current level depth based on current context
    // Always use current navigation position (breadcrumbPath/selectedRecord) as the base
    // When editing, we still use current position to determine available levels
    const currentLevelDepth = useMemo(() => {
        if (!isDrillDownMode) {
            return 0; 
        }
        
        const breadcrumbDepth = breadcrumbPath?.length || 0;
        
        
        let currentPositionDepth = breadcrumbDepth + 1; // breadcrumb shows path, current level is depth + 1
        
        // Priority 2: Try to verify/override with selectedRecord.hrOrgUnitLevels.planLevel (if available and reliable)
        // Only use it if it matches or is higher than breadcrumb depth
        if (selectedRecord?.hrOrgUnitLevels?.planLevel !== undefined && selectedRecord.hrOrgUnitLevels?.planLevel !== null) {
            const recordDepth = selectedRecord.hrOrgUnitLevels.planLevel + 1;
            if (recordDepth === currentPositionDepth || recordDepth > currentPositionDepth) {
                currentPositionDepth = recordDepth;
            }
        }
        
        return currentPositionDepth;
    }, [isDrillDownMode, selectedRecord, breadcrumbPath, levels, editData]);
    
    
    // Calculate available levels based on current level depth
    const availableLevels = useMemo(() => {
        if (currentLevelDepth <= 1) {
            return levels;
        }
        
        const minPlanLevel = currentLevelDepth - 1;
        const filteredLevels = levels.filter(level => level.planLevel >= minPlanLevel);
        return filteredLevels;
    }, [currentLevelDepth, levels, isDrillDownMode, breadcrumbPath, selectedRecord]);
    
    useEffect(() => {
        setIsIndefinite(indefiniteValue);
        if (indefiniteValue) {
            setValue('validityTo', '2099-12-31');
        } else if (watchedValidityTo === '2099-12-31') {
            setValue('validityTo', '');
        }
        trigger('validityTo');
    }, [indefiniteValue, setValue, trigger]);
 
    useEffect(() => {
        if (editData) {
            const companyId = editData.empBranchId || selectedCompany;
            setValue('companyId', companyId);
            setValue('code', editData.code);
            setValue('type', editData.type);
            setValue('segment', editData.segment);
            setValue('subordination', editData.addSubordination);
            setValue('abbreviation', editData.shortDescription);
            setValue('name', editData.longDescription);
            setValue('location', editData.locationId);
            setValue('expensesCenter', editData.empOfficeId);
            setValue('siblingPriority', editData.siblingPriority);
            if (editData.openDate) {
                setValue('validityFrom', editData.openDate);
            }
            setValue('validityTo', editData.closeDate);
            
            const isIndefiniteRecord = editData.indefinite || editData.closeDate === '2099-12-31';
            setValue('indefinite', isIndefiniteRecord);
            setIsIndefinite(isIndefiniteRecord);
            
            const typeId = editData.nomenclatureItemByTypeOfDivisionId
            const segmentId = editData.nomenclatureItemBySegmentId
            
            const levelId = levels.length > 0 
                ? levels.find(level => 
                    level.shortDescription === editData.hrOrgUnitLevels?.shortDescription || 
                    level.code === editData.hrOrgUnitLevels?.code ||
                    level.planLevel === editData.hrOrgUnitLevels?.planLevel
                  )?.id 
                : undefined;
            
            
            setValue('nomenclatureItemByTypeOfDivisionId', typeId);
            setValue('nomenclatureItemBySegmentId', segmentId);
            setValue('addSubordination', editData.addSubordination);

            if (levelId) {
                setValue('level', levelId);
            }
            const expensesCenterId = companyCenters.length > 0 ? companyCenters.find(center => center.officeNo === editData.empOffices?.officeNo)?.officeId : undefined;

            if (expensesCenterId) {
                setValue('expensesCenter', expensesCenterId);
            }

            const locationId = locations.length > 0 ? locations.find(loc => loc.name === editData.location?.name)?.id : undefined;

            if (locationId) {
                setValue('location', locationId);
            }

        } else {
            reset();
            setIsIndefinite(true);
        }
    }, [editData, levels, companyCenters, locations]);

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                setLocationsLoading(true);
                const response = await structuralGroupService.getAllLocations();

                const locationsArray = Array.isArray(response) ? response : (response.data || []);

                setLocations(locationsArray);
            } catch (error) {
                console.error('Error fetching locations:', error);
                setLocations([]);
            } finally {
                setLocationsLoading(false);
            }
        };

        fetchLocations();
    }, []);

    useEffect(() => {
        if (selectedLocationId) {
            const selectedLocation = locations.find(loc => loc.id === parseInt(selectedLocationId));
            if (selectedLocation && selectedLocation.cityById && selectedLocation.cityById.name) {
                setValue('description', selectedLocation.cityById.name);
            } else {
                setValue('description', '');
            }
        } else {
            setValue('description', '');
        }
    }, [selectedLocationId, locations]);

    useEffect(() => {
        const fetchLevels = async () => {
            if (!selectedCompany) {
                setLevels([]);
                return;
            }

            try {
                setLevelsLoading(true);
                const response = await levelOfStructureService.getAllLevels(selectedCompany);

                const levelsArray = Array.isArray(response) ? response : (response.data || []);

                setLevels(levelsArray);
            } catch (err) {
                console.error('Error fetching levels:', err);
                setLevels([]);
            } finally {
                setLevelsLoading(false);
            }
        };

        fetchLevels();
    }, [selectedCompany]);

    useEffect(() => {
        const fetchCompanyCenters = async () => {
            try {
                setCompanyCentersLoading(true);
                const response = await companiesService.getCompanyCenters();
                
                const centersArray = Array.isArray(response) ? response : (response.data || []);

                setCompanyCenters(centersArray);
            } catch (err) {
                console.error('Error fetching company centers:', err);
                setCompanyCenters([]);
            } finally {
                setCompanyCentersLoading(false);
            }
        };

        fetchCompanyCenters();
    }, []);

    useEffect(() => {
        const fetchTypeOptions = async () => {
            try {
                const options = await nomenclaturesOz.getNomenclatureItems(1,"true");
                setTypeOptions(options);
            } catch (error) {
                console.error('Error fetching type options:', error);
            }
        };
        fetchTypeOptions();
    }, []);

    useEffect(() => {
        const fetchSegmentOptions = async () => {
            try {
                const options = await nomenclaturesOz.getNomenclatureItems(2,"true");
                setSegmentOptions(options);
            } catch (error) {
                console.error('Error fetching segment options:', error);
            }
        };
        fetchSegmentOptions();
    }, []);

    useEffect(() => {
        const fetchSubordinationOptions = async () => {
            try {
                const options = await positionsService.getNomenclaturesByMnemonic('!UNIT_SUBORDINATION');
                setSubordinationOptions(options);
            } catch (error) {
                console.error('Error fetching subordination options:', error);
            }
        };
        fetchSubordinationOptions();
    }, []);

    const submitForm = async (data) => {
        setLoading(true);
        try {
            
            const { companyId, validityTo, indefinite, ...formDataWithoutCompanyId } = data;
            
            const closeDate = indefinite ? '2099-12-31' : validityTo;
            
            const formData = { 
                ...formDataWithoutCompanyId, 
                company: companyId,
                validityTo: closeDate,
                indefinite: indefinite
            };
            const finalFormData = parentId ? { ...formData, parentId } : formData;
            
            await onConfirm(finalFormData);
        } catch (error) {
            console.error('Error submitting form:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={handleClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                           {editData ? t('common.editOf') : t('common.add')} {t('organizationalStructure.structuralGroups.form.title')}
                        </h4>
                        <form id="structuralGroupForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="flex-column">
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.company')}</span>
                                    <input
                                        type="text"
                                        className="small-input"
                                        value={companyName}
                                        disabled={true}
                                        readOnly
                                    />
                                    <input
                                        type="hidden"
                                        {...register('companyId', { 
                                            value: selectedCompany || ''
                                        })}
                                    />
                                </div>
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.level')}<span className="required">*</span></span>
                                    <select
                                        className="small-select"
                                        name="level"
                                        value={watchedLevelValue || ''}
                                        {...register('level', {
                                            onChange: () => trigger('level')
                                        })}
                                    >
                                        {levelsLoading ? (
                                            <option value="">Loading levels...</option>
                                        ) : availableLevels.length === 0 ? (
                                            <option value="">No deeper levels available</option>
                                        ) : (
                                            availableLevels.map(level => {
                                                return (
                                                    <option key={level.id} value={level.id}>
                                                        {level.longDescription || level.name || level.title || `Level ${level.id}`}
                                                    </option>
                                                );
                                            })
                                        )}
                                    </select>
                                </div>
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.type')}</span>
                                    <select
                                        className="small-select"
                                        name="nomenclatureItemByTypeOfDivisionId"
                                        value={watchedNomenclatureValue || ''}
                                        {...register('nomenclatureItemByTypeOfDivisionId', {
                                            onChange: () => trigger('nomenclatureItemByTypeOfDivisionId')
                                        })}
                                    >
                                        <option value="">{t('common.select')}</option>
                                        {typeOptions.length === 0 ? (
                                            <option value="">Loading types...</option>
                                        ) : (
                                            typeOptions.map(option => (
                                                <option key={option.nomenclatureItem.id} value={option.nomenclatureItem.id}>
                                                    {option.name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.segment')}</span>
                                    <select
                                        className="small-select"
                                        name="nomenclatureItemBySegmentId"
                                        value={watchedSegmentValue || ''}
                                        {...register('nomenclatureItemBySegmentId', {
                                            onChange: () => trigger('nomenclatureItemBySegmentId')
                                        })}
                                    >
                                        <option value="">{t('common.select')}</option>
                                        {segmentOptions.length === 0 ? (
                                            <option value="">Loading segments...</option>
                                        ) : (
                                            segmentOptions.map(option => (
                                                <option key={option.nomenclatureItem.id} value={option.nomenclatureItem.id}>
                                                    {option.name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.subordination')}</span>
                                    <select 
                                        className="small-select"
                                        name="addSubordination"
                                        value={watchedSubordinationValue || ''}
                                        {...register('addSubordination', { 
                                            onChange: () => trigger('addSubordination')
                                        })}
                                    >
                                        <option value="">{t('common.select')}</option>
                                        {subordinationOptions.length === 0 ? (
                                            <option value="">Loading subordination options...</option>
                                        ) : (
                                            subordinationOptions.map(option => (
                                                <option key={option.note} value={option.id}>
                                                    {option.note}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.code')}</span>
                                    <input
                                        type="text"
                                        maxLength={20}
                                        className="small-input"
                                        {...register('code', { 
                                            onChange: () => trigger('code')
                                        })}
                                    />
                                </div>
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.abbreviation')}<span className="required">*</span></span>
                                    <input
                                        type="text"
                                        maxLength={30}
                                        className={`small-input ${errors.abbreviation ? 'input-error' : ''}`}
                                        placeholder={errors.abbreviation ? errors.abbreviation.message : t('common.required')}
                                        {...register('abbreviation', { required: true })}
                                    />
                                </div>
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.name')}<span className="required">*</span></span>
                                    <input
                                        type="text"
                                        maxLength={80}
                                        className={`small-input ${errors.name ? 'input-error' : ''}`}
                                        placeholder={errors.name ? errors.name.message : t('common.required')}
                                        {...register('name', { required: true })}
                                    />
                                </div>
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.location')}<span className="required">*</span></span>
                                    <select
                                        className={`small-select ${errors.location ? 'input-error' : ''}`}
                                        name="location"
                                        value={watchedLocationValue || ''}
                                        {...register('location', { required: t('organizationalStructure.structuralGroups.form.locationRequired') })}
                                    >
                                        {locationsLoading ? (
                                            <option value="">Loading locations...</option>
                                        ) : locations.length === 0 ? (
                                            <option value="">No locations found</option>
                                        ) : (
                                            locations.map(loc => (
                                                <option key={loc.id} value={loc.id}>
                                                    {loc.name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.city')}</span>
                                    <input
                                        type="text"
                                        className="small-input"
                                        disabled={true}
                                        {...register('description', { 
                                            onChange: () => trigger('description')
                                        })}
                                    />
                                </div>
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.costCenter')}<span className="required">*</span></span>
                                    <select
                                        className={`small-select ${errors.expensesCenter ? 'input-error' : ''}`}
                                        name="expensesCenter"
                                        value={watchedExpensesCenterValue || ''}
                                        {...register('expensesCenter', { 
                                            onChange: () => trigger('expensesCenter')
                                        })}
                                    >
                                        {companyCentersLoading ? (
                                            <option value="">Loading centers...</option>
                                        ) : companyCenters.length === 0 ? (
                                            <option value="">No centers found</option>
                                        ) : (
                                            companyCenters.map(center => (
                                                <option key={center.officeId} value={center.officeId}>
                                                    {center.name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.priority')}<span className="required">*</span></span>
                                    <input 
                                        type="number" 
                                        maxLength={20}
                                        onInput={(e) => {
                                            if (e.target.value.length > 20) {
                                              e.target.value = e.target.value.slice(0, 20);
                                            }
                                          }}
                                        className={`small-input ${errors.siblingPriority ? 'input-error' : ''}`}
                                        placeholder={errors.siblingPriority ? errors.siblingPriority.message : t('common.required')}
                                        {...register('siblingPriority', { required: true })} 
                                    />
                                </div>
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.fromDate')}<span className="required">*</span></span>
                                    <CustomDatePicker
                                        name="validityFrom"
                                        value={watch('validityFrom')}
                                        onChange={(date) => {
                                            setValue('validityFrom', date);
                                        }}
                                        isSmall={true}
                                    />
                                </div>
                                {!indefiniteValue && (
                                    <div className="flex-row margin-bottom-5">
                                        <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.toDate')}<span className="required">*</span></span>
                                        <CustomDatePicker
                                            name="validityTo"
                                            value={watch('validityTo') === '2099-12-31' ? '' : watch('validityTo')}
                                            minDate={watch('validityFrom') || new Date()}
                                            onChange={(date) => {
                                                setValue('validityTo', date);
                                                if (date && indefiniteValue) {
                                                    setValue('indefinite', false);
                                                }
                                                trigger('validityTo');
                                            }}
                                            isSmall={true}
                                        />
                                    </div>
                                )}
                                <div className="flex-row margin-bottom-5">
                                    <span className="span-bold no-wrap form-label-fixed">{t('organizationalStructure.structuralGroups.form.indefinite')}</span>
                                    <div className="checkbox-wrapper-start title-checkbox">
                                        <input
                                            type="checkbox"
                                            id="indefinite"
                                            {...register('indefinite')}
                                        />
                                        <label htmlFor="indefinite">{t('organizationalStructure.structuralGroups.form.indefinite')}</label>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <LoadingButton type="submit" className="btn btn-primary" loading={loading} disabled={loading}>
                                    {t('common.save')}
                                </LoadingButton>
                                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                    {t('common.cancel')}
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}